import React, { useEffect, useState } from "react";
import { Plus, Edit, Trash2, X } from "lucide-react";
import api, { fmtError } from "@/lib/api";
import { SF_Picker } from "./AdminLayout";
import { toast } from "sonner";

const BLANK = {
  storefront: "jewelry", name: "", sku: "", description: "", short_description: "",
  price_usd: 0, sale_price_usd: null, images: [""], category: "", collections: [],
  tags: [], weight_g: 0, materials: "", country_of_origin: "Canada", hs_code: "",
  sizes: [], colors: [], inventory_qty: 0, low_stock_threshold: 5, metal: "",
  featured: false, visible: true,
};

export default function AdminProducts() {
  const [sf, setSf] = useState("jewelry");
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/products", { params: { storefront: sf } }).then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, [sf]);

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Product deleted."); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-3xl">Products</h1>
        <div className="flex gap-3">
          <SF_Picker value={sf} onChange={setSf} />
          <button data-testid="admin-add-product" onClick={() => setEditing({ ...BLANK, storefront: sf })} className="flex items-center gap-2 bg-neutral-900 text-white px-5 h-10 rounded text-sm"><Plus size={16} /> Add Product</button>
        </div>
      </div>

      {products.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-lg p-16 text-center text-neutral-500">No products yet. Add your first piece.</div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-500 text-xs uppercase tracking-wide">
              <tr><th className="text-left p-4">Product</th><th className="text-left p-4">SKU</th><th className="text-left p-4">Price</th><th className="text-left p-4">Stock</th><th className="text-left p-4">Status</th><th className="p-4"></th></tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t border-neutral-100" data-testid={`admin-product-row-${p.sku}`}>
                  <td className="p-4 flex items-center gap-3"><img src={(p.images || [])[0]} alt="" className="w-10 h-12 object-cover rounded" /><span className="font-medium">{p.name}</span></td>
                  <td className="p-4 text-neutral-500">{p.sku}</td>
                  <td className="p-4">${p.price_usd}</td>
                  <td className="p-4">{p.inventory_qty <= p.low_stock_threshold ? <span className="text-amber-600">{p.inventory_qty} low</span> : p.inventory_qty}</td>
                  <td className="p-4">{p.visible ? <span className="text-green-600">Visible</span> : <span className="text-neutral-400">Hidden</span>}{p.featured && <span className="ml-2 text-xs bg-neutral-100 px-2 py-0.5 rounded">Featured</span>}</td>
                  <td className="p-4 text-right whitespace-nowrap">
                    <button data-testid={`admin-edit-${p.sku}`} onClick={() => setEditing(p)} className="p-2 hover:bg-neutral-100 rounded"><Edit size={15} /></button>
                    <button data-testid={`admin-delete-${p.sku}`} onClick={() => del(p.id)} className="p-2 hover:bg-neutral-100 rounded text-red-500"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <ProductModal product={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ProductModal({ product, onClose, onSaved }) {
  const [f, setF] = useState({ ...product });
  const [uploading, setUploading] = useState(false);
  const [cols, setCols] = useState([]);
  useEffect(() => {
    if (f.storefront) api.get(`/storefront/${f.storefront}/bootstrap`).then((r) => setCols(r.data.collections || [])).catch(() => setCols([]));
  }, [f.storefront]);
  const set = (k, v) => setF({ ...f, [k]: v });
  const list = (v) => (Array.isArray(v) ? v.join(", ") : v || "");
  const toList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
  const og = f.option_groups || [];
  const setOG = (v) => set("option_groups", v);
  const addGroup = () => setOG([...og, { id: `g${Date.now()}`, heading: "Option", description: "", type: "buttons", required: false, enabled: true, choices: [] }]);
  const delGroup = (gi) => { if (window.confirm("Delete this option group?")) setOG(og.filter((_, i) => i !== gi)); };
  const updGroup = (gi, k, v) => setOG(og.map((g, i) => i === gi ? { ...g, [k]: v } : g));
  const addChoice = (gi) => setOG(og.map((g, i) => i === gi ? { ...g, choices: [...(g.choices || []), { name: "Choice", price_usd: 0, default: false }] } : g));
  const updChoice = (gi, ci, k, v) => setOG(og.map((g, i) => i === gi ? { ...g, choices: g.choices.map((c, j) => j === ci ? { ...c, [k]: v } : c) } : g));
  const delChoice = (gi, ci) => setOG(og.map((g, i) => i === gi ? { ...g, choices: g.choices.filter((_, j) => j !== ci) } : g));

  const handleUpload = async (e) => {
    const files = [...e.target.files];
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((file) => fd.append("files", file));
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const base = process.env.REACT_APP_BACKEND_URL;
      const urls = data.urls.map((u) => (u.startsWith("http") ? u : `${base}${u}`));
      setF((prev) => ({ ...prev, images: [...(prev.images || []).filter(Boolean), ...urls] }));
      toast.success(`${urls.length} photo${urls.length > 1 ? "s" : ""} uploaded.`);
    } catch (err) {
      toast.error(fmtError(err.response?.data?.detail) || "Upload failed. Please try again.");
    }
    setUploading(false);
    e.target.value = "";
  };

  const save = async () => {
    const body = { ...f, price_usd: Number(f.price_usd), inventory_qty: Number(f.inventory_qty), weight_g: 0,
      sale_price_usd: f.sale_price_usd ? Number(f.sale_price_usd) : null,
      images: (f.images || []).filter(Boolean) };
    try {
      if (f.id) await api.put(`/admin/products/${f.id}`, body);
      else await api.post("/admin/products", body);
      toast.success("Product saved."); onSaved();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
  };

  const inp = "w-full h-10 px-3 border border-neutral-300 rounded text-sm";
  const lbl = "block text-xs uppercase tracking-wide text-neutral-500 mb-1 mt-3";

  return (
    <div className="fixed inset-0 bg-black/50 z-[200] flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto p-8" onClick={(e) => e.stopPropagation()} data-testid="admin-product-modal">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl">{f.id ? "Edit Product" : "Add Product"}</h2>
          <button onClick={onClose}><X size={22} /></button>
        </div>
        <div className="grid grid-cols-2 gap-x-4">
          <div className="col-span-2"><label className={lbl}>Name</label><input data-testid="pf-name" className={inp} value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
          <div><label className={lbl}>SKU</label><input data-testid="pf-sku" className={inp} value={f.sku} onChange={(e) => set("sku", e.target.value)} /></div>
          <div><label className={lbl}>Storefront</label><select className={inp} value={f.storefront} onChange={(e) => set("storefront", e.target.value)}><option value="jewelry">Jewelry</option><option value="men">Men</option><option value="women">Women</option></select></div>
          <div><label className={lbl}>Price (USD base)</label><input data-testid="pf-price" type="number" className={inp} value={f.price_usd} onChange={(e) => set("price_usd", e.target.value)} /></div>
          <div><label className={lbl}>Sale Price (USD)</label><input type="number" className={inp} value={f.sale_price_usd || ""} onChange={(e) => set("sale_price_usd", e.target.value)} /></div>
          <div><label className={lbl}>Pricing Mode</label><select data-testid="pf-pricing-mode" className={inp} value={f.pricing_mode || "fixed"} onChange={(e) => set("pricing_mode", e.target.value)}><option value="fixed">Fixed</option><option value="from">Starting price (From $X)</option><option value="range">Price range</option></select></div>
          <div><label className={lbl}>Display Orientation</label><select data-testid="pf-orientation" className={inp} value={f.orientation || "portrait"} onChange={(e) => set("orientation", e.target.value)}><option value="portrait">Portrait</option><option value="landscape">Landscape</option></select></div>
          {f.pricing_mode === "range" && (<>
            <div><label className={lbl}>Min Price (USD)</label><input type="number" className={inp} value={f.price_min_usd || ""} onChange={(e) => set("price_min_usd", Number(e.target.value))} /></div>
            <div><label className={lbl}>Max Price (USD)</label><input type="number" className={inp} value={f.price_max_usd || ""} onChange={(e) => set("price_max_usd", Number(e.target.value))} /></div>
          </>)}
          <div><label className={lbl}>Category</label><input className={inp} value={f.category} onChange={(e) => set("category", e.target.value)} /></div>
          <div><label className={lbl}>Metal (jewelry)</label><input className={inp} value={f.metal || ""} onChange={(e) => set("metal", e.target.value)} placeholder="gold / silver" /></div>
          <div className="col-span-2"><label className={lbl}>Short Description</label><input className={inp} value={f.short_description} onChange={(e) => set("short_description", e.target.value)} /></div>
          <div className="col-span-2"><label className={lbl}>Description</label><textarea className={`${inp} h-24 py-2`} value={f.description} onChange={(e) => set("description", e.target.value)} /></div>
          <div className="col-span-2"><label className={lbl}>Product Photos</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {(f.images || []).filter(Boolean).map((url, idx) => (
                <div key={idx} className="relative w-20 h-24">
                  <img src={url} alt="" className="w-full h-full object-cover rounded border border-neutral-200" />
                  <button type="button" data-testid={`pf-remove-img-${idx}`} onClick={() => setF((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }))}
                    className="absolute -top-2 -right-2 bg-neutral-900 text-white rounded-full w-5 h-5 text-xs leading-none">×</button>
                </div>
              ))}
            </div>
            <label className="inline-flex items-center gap-2 border border-neutral-300 rounded px-4 h-10 text-sm cursor-pointer hover:bg-neutral-50">
              <input data-testid="pf-upload" type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
              {uploading ? "Uploading…" : "Upload Photos"}
            </label>
            <span className="text-xs text-neutral-400 ml-3">JPG / PNG / WebP · up to 8MB each</span>
          </div>
          <div className="col-span-2"><label className={lbl}>Categories / Menu Areas (from this storefront's front-page menu)</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {(f.collections || []).map((slug) => {
                const c = cols.find((x) => x.slug === slug);
                return (
                  <span key={slug} data-testid={`pf-cat-chip-${slug}`} className="inline-flex items-center gap-1.5 bg-neutral-100 px-3 py-1 rounded text-sm capitalize">
                    {c ? c.name : slug}
                    <button type="button" onClick={() => set("collections", (f.collections || []).filter((s) => s !== slug))} className="text-neutral-500 leading-none">×</button>
                  </span>
                );
              })}
              {(f.collections || []).length === 0 && <span className="text-xs text-neutral-400">No category selected yet</span>}
            </div>
            <select data-testid="pf-collection" className={inp} value="" onChange={(e) => { if (e.target.value) set("collections", [...(f.collections || []), e.target.value]); }}>
              <option value="">+ Add to a category…</option>
              {cols.filter((c) => !(f.collections || []).includes(c.slug)).map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
          </div>
          <div><label className={lbl}>Tags (comma sep)</label><input className={inp} value={list(f.tags)} onChange={(e) => set("tags", toList(e.target.value))} /></div>
          <div><label className={lbl}>Sizes (comma sep)</label><input className={inp} value={list(f.sizes)} onChange={(e) => set("sizes", toList(e.target.value))} /></div>
          <div><label className={lbl}>Colours (comma sep)</label><input className={inp} value={list(f.colors)} onChange={(e) => set("colors", toList(e.target.value))} /></div>
          <div><label className={lbl}>Inventory Qty</label><input data-testid="pf-qty" type="number" className={inp} value={f.inventory_qty} onChange={(e) => set("inventory_qty", e.target.value)} /></div>
          <div><label className={lbl}>Materials</label><input className={inp} value={f.materials} onChange={(e) => set("materials", e.target.value)} /></div>
          <div><label className={lbl}>Country of Origin — internal</label><input className={inp} value={f.country_of_origin} onChange={(e) => set("country_of_origin", e.target.value)} /></div>
          <div><label className={lbl}>HS / Tariff Code — internal</label><input className={inp} value={f.hs_code} onChange={(e) => set("hs_code", e.target.value)} /></div>
          <div className="col-span-2 flex gap-6 mt-4">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.visible} onChange={(e) => set("visible", e.target.checked)} /> Visible</label>
          </div>
          <div className="col-span-2 border-t border-neutral-200 pt-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium">Product Options & Customization</span>
              <button type="button" data-testid="pf-add-group" onClick={addGroup} className="text-xs border border-neutral-300 rounded px-3 h-8">+ Add Option Group</button>
            </div>
            {og.length === 0 && <p className="text-xs text-neutral-400">No custom options. This product uses its standard price.</p>}
            {og.map((g, gi) => (
              <div key={gi} className="border border-neutral-200 rounded p-3 mb-3" data-testid={`pf-group-${gi}`}>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <input className={inp} placeholder="Group heading" value={g.heading || ""} onChange={(e) => updGroup(gi, "heading", e.target.value)} />
                  <select className={inp} value={g.type || "buttons"} onChange={(e) => updGroup(gi, "type", e.target.value)}><option value="buttons">Buttons / Swatches</option><option value="dropdown">Dropdown</option></select>
                </div>
                <input className={`${inp} mb-2`} placeholder="Description (optional)" value={g.description || ""} onChange={(e) => updGroup(gi, "description", e.target.value)} />
                <div className="flex items-center gap-4 mb-2 text-sm">
                  <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!g.required} onChange={(e) => updGroup(gi, "required", e.target.checked)} /> Required</label>
                  <label className="flex items-center gap-1.5"><input type="checkbox" checked={g.enabled !== false} onChange={(e) => updGroup(gi, "enabled", e.target.checked)} /> Enabled</label>
                  <button type="button" onClick={() => delGroup(gi)} className="text-red-500 text-xs ml-auto">Delete group</button>
                </div>
                {(g.choices || []).map((c, ci) => (
                  <div key={ci} className="flex items-center gap-2 mb-1.5">
                    <input className="flex-1 h-9 px-2 border border-neutral-300 rounded text-sm" placeholder="Choice name" value={c.name} onChange={(e) => updChoice(gi, ci, "name", e.target.value)} />
                    <input className="w-28 h-9 px-2 border border-neutral-300 rounded text-sm" type="number" placeholder="+$ adj" value={c.price_usd} onChange={(e) => updChoice(gi, ci, "price_usd", Number(e.target.value))} />
                    <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={!!c.default} onChange={(e) => updChoice(gi, ci, "default", e.target.checked)} /> Default</label>
                    <button type="button" onClick={() => delChoice(gi, ci)} className="text-red-500 text-xs">×</button>
                  </div>
                ))}
                <button type="button" data-testid={`pf-add-choice-${gi}`} onClick={() => addChoice(gi)} className="text-xs text-neutral-600 mt-1">+ Add choice</button>
              </div>
            ))}
          </div>
        </div>
        <button data-testid="pf-save" onClick={save} className="mt-8 w-full bg-neutral-900 text-white h-11 rounded text-sm tracking-wide uppercase">Save Product</button>
      </div>
    </div>
  );
}
